import { toHoverColor, toPressColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HoverView, Pad, PadBox, VertBox } from 'component/basics';
import { Heading, UtilityText } from 'component/text';
import { View } from 'react-native';
import type { TLabel } from 'util/stdtypes';

export type DashboardNavDrawerTab = {
    key: string;
    label?: TLabel;
    text?: string;
    icon: React.ComponentType<{ color?: string; size?: number }>;
    testID?: string;
};

type NavButtonProps = {
    tab: DashboardNavDrawerTab;
    selected: boolean;
    onPress: () => void;
};

function NavButton({ tab, selected, onPress }: NavButtonProps) {
    const { colors } = useTheme();
    const s = useNavButtonStyles();

    return (
        <HoverView
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            style={[s.tab, selected ? s.selectedTab : {}]}
            testID={tab.testID ?? tab.key}
        >
            <tab.icon color={colors.colorTextPrimary} size={20} />
            <Pad size={12} />
            <UtilityText label={tab.label} text={tab.text} type='large' weight={selected ? 'medium' : 'regular'} />
        </HoverView>
    );
}

const useNavButtonStyles = createThemedStyles((tokens) => ({
    tab: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 44,
        paddingHorizontal: 12,
        borderRadius: 32,
        backgroundColor: tokens.colors.colorButtonSecondary,
    },
    hover: {
        backgroundColor: toHoverColor(tokens.colors.colorButtonSecondary),
    },
    pressed: {
        backgroundColor: toPressColor(tokens.colors.colorButtonSecondary),
    },
    selectedTab: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 32,
    },
}));

export type DashboardNavDrawerProps = {
    tabs: DashboardNavDrawerTab[];
    titleLabel?: TLabel;
    titleText?: string;
    selectedTabKey?: string;
    onTabPress: (tabKey: string) => void;
};

export function DashboardNavDrawer({ tabs, titleLabel, titleText, selectedTabKey, onTabPress }: DashboardNavDrawerProps) {
    const s = useDashboardNavDrawerStyles();
    return (
        <View style={s.container}>
            <PadBox horiz={40} vert={40}>
                <Heading label={titleLabel} text={titleText} />
                <Pad size={40} />
                <VertBox gap={16} wide>
                    {tabs.map((tab) => (
                        <NavButton key={tab.key} onPress={() => onTabPress(tab.key)} selected={tab.key === selectedTabKey} tab={tab} />
                    ))}
                </VertBox>
            </PadBox>
        </View>
    );
}

const useDashboardNavDrawerStyles = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderRightColor: tokens.colors.colorBorderPrimary,
        borderRightWidth: 1,
        width: 300,
    },
}));
